import * as React from "react";
import { cn } from "@/shared/lib/cn";

export interface SidebarProps extends React.ComponentPropsWithoutRef<"aside"> {
  collapsed?: boolean;
  mobileOpen?: boolean;
}

export const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    { collapsed = false, mobileOpen = false, className, children, ...props },
    ref
  ) => (
    <aside
      ref={ref}
      data-state={collapsed ? "collapsed" : "expanded"}
      data-mobile-state={mobileOpen ? "open" : "closed"}
      className={cn(
        "group/sidebar relative z-50 flex w-72 flex-col overflow-visible border-r border-white/10 bg-carbon-900/90 text-white shadow-[0_0_60px_-30px_rgba(0,59,153,0.55)] backdrop-blur-xl",
        "transition-[width,transform,opacity] duration-300 ease-in-out",
        "supports-backdrop-filter:bg-carbon-900/75",
        "fixed inset-y-0 left-0 -translate-x-full md:sticky md:top-0 md:z-20 md:h-screen md:translate-x-0",
        collapsed ? "md:w-20" : "md:w-72",
        mobileOpen && "translate-x-0",
        className
      )}
      {...props}
    >
      {children}
    </aside>
  )
);
Sidebar.displayName = "Sidebar";

export const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex h-20 shrink-0 items-center justify-between px-5",
      className
    )}
    {...props}
  />
));
SidebarHeader.displayName = "SidebarHeader";

export const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-1 flex-col gap-8 overflow-y-auto px-4 pb-10 pt-4",
      "scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent",
      className
    )}
    {...props}
  />
));
SidebarContent.displayName = "SidebarContent";

export const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("px-4 pb-6 pt-2 text-xs text-white/60", className)}
    {...props}
  />
));
SidebarFooter.displayName = "SidebarFooter";

export interface SidebarRailToggleProps
  extends React.ComponentPropsWithoutRef<"button"> {
  collapsed?: boolean;
}

export const SidebarRailToggle = React.forwardRef<
  HTMLButtonElement,
  SidebarRailToggleProps
>(({ collapsed = false, className, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    data-state={collapsed ? "collapsed" : "expanded"}
    className={cn(
      "hidden z-60 md:flex md:absolute md:-right-4 md:top-22 md:z-60 md:h-9 md:w-9 md:items-center md:justify-center",
      "md:rounded-full md:border md:border-carbon-900/30 md:bg-white md:text-imperial-600",
      "md:shadow-[0_18px_40px_-16px_rgba(0,44,115,0.55),0_0_14px_rgba(0,59,153,0.45),0_0_0_1px_rgba(0,17,34,0.25)] md:backdrop-blur-sm",
      "md:transition md:hover:border-carbon-900/10 md:hover:bg-white md:hover:text-imperial-700 md:hover:shadow-[0_20px_46px_-18px_rgba(0,44,115,0.65),0_0_18px_rgba(0,59,153,0.55),0_0_0_1px_rgba(0,17,34,0.35)]",
      "md:focus-visible:outline-2 md:focus-visible:outline-offset-2 md:focus-visible:outline-imperial-500",
      className
    )}
    {...props}
  >
    {children}
  </button>
));
SidebarRailToggle.displayName = "SidebarRailToggle";
